import React, { useEffect, useMemo, useState } from 'react';
import Button from '../ui/Button';
import Card from '../ui/Card';
import { RefreshCw, Search, ChevronLeft, ChevronRight, Download } from 'lucide-react';

const PagedPaymentsTable = () => {
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50); // default 50 per your choice
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all'); // All / Successful / Failed / Refunded
  const [start, setStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30); // last 30 days default
    return d.toISOString();
  });
  const [end, setEnd] = useState(() => new Date().toISOString());

  const headerCell = (label, key) => (
    <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 12, color: '#6b7280', cursor: 'pointer' }}
        onClick={() => {
          const nextDir = sortBy === key && sortDir === 'desc' ? 'asc' : 'desc';
          setSortBy(key); setSortDir(nextDir); fetchPage(page, key, nextDir, pageSize, search, status);
        }}>
      {label} {sortBy === key ? (sortDir === 'desc' ? '▼' : '▲') : ''}
    </th>
  );

  const fetchPage = async (p = page, sBy = sortBy, sDir = sortDir, size = pageSize, query = search, st = status, s = start, e = end) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const url = new URL(`${API_BASE_URL}/api/admin/reports/payments/paged`);
      url.searchParams.set('page', String(p));
      url.searchParams.set('page_size', String(size));
      url.searchParams.set('sort_by', sBy);
      url.searchParams.set('sort_dir', sDir);
      if (query && query.trim()) url.searchParams.set('q', query.trim());
      if (st) url.searchParams.set('status', st);
      if (s) url.searchParams.set('start_date', s);
      if (e) url.searchParams.set('end_date', e);
      const resp = await fetch(url.toString(), { headers: { 'Authorization': `Bearer ${token}` } });
      if (!resp.ok) throw new Error('Failed to fetch payments');
      const data = await resp.json();
      setItems(data.items || []);
      setTotal(data.total || 0);
      setPage(data.page || 1);
      setPageSize(data.page_size || size);
      setSortBy(data.sort_by || sBy);
      setSortDir(data.sort_dir || sDir);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPage(1, 'created_at', 'desc', 50, '', 'all'); }, []);

  const pages = Math.ceil(total / pageSize) || 1;

  const exportXlsx = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const url = new URL(`${API_BASE_URL}/api/admin/reports/payments/export`);
      if (search && search.trim()) url.searchParams.set('q', search.trim());
      if (status) url.searchParams.set('status', status);
      if (start) url.searchParams.set('start_date', start);
      if (end) url.searchParams.set('end_date', end);
      const resp = await fetch(url.toString(), { headers: { 'Authorization': `Bearer ${token}` } });
      if (!resp.ok) throw new Error('Failed to export payments');
      const blob = await resp.blob();
      const a = document.createElement('a');
      const urlBlob = URL.createObjectURL(blob);
      a.href = urlBlob;
      a.download = 'payments.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(urlBlob);
    } catch (e) {
      console.error(e);
      alert(e.message || 'Export failed');
    }
  };

  const formatDate = (s) => {
    // Backend now returns pre-formatted strings (dd/mm/yyyy HH:MM:SS), just validate and return
    if (!s) return '';
    if (typeof s === 'string' && s.includes('/')) return s;  // Already formatted by backend
    // Fallback: try to parse and format to dd/mm/yyyy HH:MM:SS
    try {
      const d = new Date(s);
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      const hours = String(d.getHours()).padStart(2, '0');
      const minutes = String(d.getMinutes()).padStart(2, '0');
      const seconds = String(d.getSeconds()).padStart(2, '0');
      return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    } catch {
      return s;
    }
  };

  return (
    <Card>
      <Card.Header>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Card.Title>Payments</Card.Title>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ position: 'relative' }}>
              <input
                placeholder="Search payment/order/user"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') fetchPage(1, sortBy, sortDir, pageSize, e.target.value, status); }}
                style={{ padding: '8px 28px 8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, width: 260 }}
              />
              <Search size={14} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
            </div>
            <select value={status} onChange={(e) => { setStatus(e.target.value); fetchPage(1, sortBy, sortDir, pageSize, search, e.target.value); }} style={{ padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14 }}>
              <option value="all">All</option>
              <option value="successful">Successful</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
            </select>
            <input type="date" value={start.split('T')[0]} onChange={(e) => setStart(new Date(e.target.value).toISOString())} style={{ padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8 }} />
            <input type="date" value={end.split('T')[0]} onChange={(e) => setEnd(new Date(e.target.value).toISOString())} style={{ padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8 }} />
            <Button variant="secondary" onClick={() => fetchPage(1, sortBy, sortDir, pageSize, search, status, start, end)} icon={<RefreshCw size={14} />}>Refresh</Button>
            <Button variant="primary" onClick={exportXlsx} icon={<Download size={14} />}>Export XLSX</Button>
          </div>
        </div>
      </Card.Header>
      <Card.Content>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead>
              <tr>
                {headerCell('Date', 'created_at')}
                <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 12, color: '#6b7280' }}>Payment ID</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 12, color: '#6b7280' }}>Order ID</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 12, color: '#6b7280' }}>User Email</th>
                {headerCell('Currency', 'currency')}
                {headerCell('Amount', 'amount')}
                {headerCell('Status', 'status')}
                <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 12, color: '#6b7280' }}>Method</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 12, color: '#6b7280' }}>Fees</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 12, color: '#6b7280' }}>Tax</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 12, color: '#6b7280' }}>Net</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 12, color: '#6b7280' }}>Reconciled At</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="13" style={{ padding: 20, textAlign: 'center', color: '#6b7280' }}>Loading...</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan="13" style={{ padding: 20, textAlign: 'center', color: '#6b7280' }}>No payments found</td></tr>
              ) : (
                items.map((row, idx) => (
                  <tr key={row.payment_id || idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '10px 12px' }}>{row.date}</td>
                    <td style={{ padding: '10px 12px' }}>{row.payment_id}</td>
                    <td style={{ padding: '10px 12px' }}>{row.order_id}</td>
                    <td style={{ padding: '10px 12px' }}>{row.user_email}</td>
                    <td style={{ padding: '10px 12px' }}>{row.currency}</td>
                    <td style={{ padding: '10px 12px' }}>{Number(row.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td style={{ padding: '10px 12px' }}>{row.status}</td>
                    <td style={{ padding: '10px 12px' }}>{row.method || ''}</td>
                    <td style={{ padding: '10px 12px' }}>{Number(row.fees || 0).toFixed(2)}</td>
                    <td style={{ padding: '10px 12px' }}>{Number(row.tax || 0).toFixed(2)}</td>
                    <td style={{ padding: '10px 12px' }}>{Number(row.net || row.amount || 0).toFixed(2)}</td>
                    <td style={{ padding: '10px 12px' }}>{formatDate(row.reconciled_at)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
          <div style={{ fontSize: 12, color: '#6b7280' }}>
            Page {page} of {pages} • {total} total
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" onClick={() => { if (page > 1) { const np = page - 1; setPage(np); fetchPage(np, sortBy, sortDir, pageSize, search, status); } }} disabled={page === 1} icon={<ChevronLeft size={14} />}>Prev</Button>
            <Button variant="secondary" onClick={() => { if (page < pages) { const np = page + 1; setPage(np); fetchPage(np, sortBy, sortDir, pageSize, search, status); } }} disabled={page >= pages} icon={<ChevronRight size={14} />}>Next</Button>
          </div>
        </div>
      </Card.Content>
    </Card>
  );
};

export default PagedPaymentsTable;